import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  api,
  type AgentRow,
  type CustomDateRange,
  type MyTeam,
  type RangeMode,
  type TimeRange,
  type UserOut,
} from "../lib/api";
import { TimeRangeControls } from "../components/TimeRangeControls";
import { Tooltip } from "../components/Tooltip";
import { LabelWithTooltip } from "../components/MetricTooltips";
import { fmtMinutesOneDp } from "../lib/format";
import { buildUrl, parseUrl } from "../lib/router";
import { useTimeRange } from "../lib/useTimeRange";
import { agentLookupKey, agentSlug } from "../lib/agents.helpers";
import { AgentAvatar, BRAND_OPTIONS, BrandPill } from "../lib/agents";

type MetricKind = "quality" | "positive" | "negative";

// Brand semantic thresholds: green ≥80%, amber 50–79%, red <50%.
const METRIC_THRESHOLDS: Record<MetricKind, { green: number; amber: number }> = {
  quality: { green: 80, amber: 50 },
  positive: { green: 80, amber: 50 },
  negative: { green: 80, amber: 50 },
};

function pctColor(pct: number, kind: MetricKind): string {
  // Negative %: any occurrence is a warning signal — mirror the Agent
  // Profile's negative sentiment bar treatment (red when present,
  // muted when zero).
  if (kind === "negative") return pct > 0 ? "bg-destructive" : "bg-muted";
  const t = METRIC_THRESHOLDS[kind];
  if (pct >= t.green) return "bg-primary";
  if (pct >= t.amber) return "bg-accent";
  return "bg-destructive";
}

function MetricBar({ pct, kind }: { pct: number; kind: MetricKind }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 rounded bg-muted overflow-hidden">
        <div
          className={`h-full ${pctColor(clamped, kind)}`}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground tabular-nums w-9 text-right">
        {Math.round(clamped)}%
      </span>
    </div>
  );
}

type AgentSortKey =
  | "calls"
  | "name"
  | "avg_duration_ms"
  | "quality_pct"
  | "positive_pct"
  | "negative_pct";

const SORT_LABELS: Record<AgentSortKey, string> = {
  calls: "Calls",
  name: "Name",
  avg_duration_ms: "Avg duration",
  quality_pct: "Quality call %",
  positive_pct: "Positive %",
  negative_pct: "Negative %",
};

type CompareSubject = {
  id: string;
  kind: "agent";
  name: string;
  sublabel: string | null;
  // Task #377 — Total Calls is pinned at the top of every Compare
  // Agents card. Counts every call (viable or not) attributed to the
  // agent in the period.
  total_calls: number;
  calls: number;
  avg_duration_ms: number | null;
  quality_pct: number | null;
  positive_pct: number | null;
  negative_pct: number | null;
  // Task #377 — % of analysed (viable) calls with at least one
  // detected "conversion" label from the Client Conversion
  // assessment. ``null`` when no calls or the agent's team isn't
  // enrolled in any Client Conversion assessment.
  conversion_pct: number | null;
};

type CompareSortKey =
  | "total_calls"
  | "calls"
  | "avg_duration_ms"
  | "quality_pct"
  | "positive_pct"
  | "negative_pct"
  | "conversion_pct";

function CompareAgentsPanel({
  filteredAgents,
  myTeams,
  timeRange,
  rangeMode,
  customRange,
}: {
  filteredAgents: AgentRow[];
  myTeams: MyTeam[];
  timeRange: TimeRange;
  rangeMode: RangeMode;
  // Task #365 — forwarded so the per-team-agents fetch the panel
  // performs honours the same custom window the outer agents list is
  // viewing.
  customRange?: CustomDateRange | null;
}) {
  const [open, setOpen] = useState(false);
  // Task #377 — the standalone Compare Teams mode is gone. Only
  // ``agents`` and ``team_agents`` remain. The setter is typed to
  // accept the wider stale-state union so we can defensively snap a
  // persisted ``"teams"`` back to ``"agents"`` (see ``switchMode``).
  const [mode, setMode] = useState<"agents" | "team_agents">("team_agents");
  const [selectedAgentKeys, setSelectedAgentKeys] = useState<string[]>([]);
  const [selectedTeamForAgents, setSelectedTeamForAgents] = useState<string>("");
  const [agentSearch, setAgentSearch] = useState("");
  const [compareSortKey, setCompareSortKey] =
    useState<CompareSortKey>("calls");
  const [compareSortDir, setCompareSortDir] = useState<"asc" | "desc">("desc");

  const agentByKey = useMemo(() => {
    const m = new Map<string, AgentRow>();
    for (const r of filteredAgents) {
      const k = agentLookupKey(r);
      if (k) m.set(k, r);
    }
    return m;
  }, [filteredAgents]);

  const agentSearchResults = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    const selected = new Set(selectedAgentKeys);
    const candidates = filteredAgents.filter((r) => {
      const k = agentLookupKey(r);
      if (!k || selected.has(k)) return false;
      if (!q) return true;
      const name = (r.full_name || "").toLowerCase();
      const email = (r.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
    return candidates.slice(0, 8);
  }, [filteredAgents, agentSearch, selectedAgentKeys]);

  const teamAgentsQuery = useQuery<AgentRow[]>({
    queryKey: [
      "agents",
      selectedTeamForAgents || "__none__",
      timeRange,
      rangeMode,
      customRange?.start ?? null,
      customRange?.end ?? null,
    ],
    queryFn: () =>
      api.agents(
        selectedTeamForAgents,
        timeRange,
        rangeMode,
        customRange,
      ),
    enabled: mode === "team_agents" && !!selectedTeamForAgents,
    refetchInterval: false,
  });

  // Task #428 — single-team users skip the dropdown entirely: when in
  // team mode and exactly one team is assigned, auto-select it so its
  // agents load immediately. Multi-team users (length !== 1) keep the
  // manual dropdown. The ``!selectedTeamForAgents`` guard means we only
  // fill an empty selection — switching tabs / Reset clears it and this
  // re-selects, but it never clobbers a deliberate pick. The team list
  // is fetched async, so this also fires once ``myTeams`` resolves.
  useEffect(() => {
    const onlyTeam = myTeams.length === 1 ? myTeams[0] : undefined;
    if (mode === "team_agents" && onlyTeam && !selectedTeamForAgents) {
      setSelectedTeamForAgents(onlyTeam.id);
    }
  }, [mode, myTeams, selectedTeamForAgents]);

  const resetSelections = () => {
    setSelectedAgentKeys([]);
    setSelectedTeamForAgents("");
    setAgentSearch("");
  };

  const reset = () => {
    resetSelections();
    setMode("team_agents");
    setOpen(false);
  };

  const switchMode = (next: "agents" | "team_agents") => {
    if (next === mode) return;
    resetSelections();
    setMode(next);
  };

  // Task #377 — guard against stale state landing on the removed
  // ``"teams"`` mode (e.g. older persisted state, an in-flight
  // history entry). Snap back to ``"team_agents"`` — the Task #428
  // default — so the panel keeps rendering instead of flickering
  // onto a tab that no longer exists in the JSX.
  if ((mode as string) === "teams") {
    setMode("team_agents");
  }

  const teamAgentsRows: AgentRow[] = useMemo(() => teamAgentsQuery.data ?? [], [teamAgentsQuery.data]);

  // Task #377 — builds a CompareSubject for an AgentRow, including
  // the new Total Calls + Conversion % metrics. ``conversion_pct``
  // stays ``null`` when the backend reports ``converting_count = null``
  // (team isn't enrolled in any Client Conversion assessment) or when
  // the agent has zero analysed calls — both render as "—".
  const subjectFromAgent = (r: AgentRow, idPrefix: string, key: string): CompareSubject => ({
    id: `${idPrefix}:${key}`,
    kind: "agent",
    name: r.full_name || r.email || "—",
    sublabel: r.brand || null,
    total_calls: r.total_calls,
    calls: r.calls,
    avg_duration_ms: r.avg_duration_ms,
    quality_pct: r.calls ? (r.quality_call_count / r.calls) * 100 : null,
    positive_pct: r.calls ? (r.positive_count / r.calls) * 100 : null,
    negative_pct: r.calls ? (r.negative_count / r.calls) * 100 : null,
    conversion_pct:
      r.converting_count == null || r.calls === 0
        ? null
        : (r.converting_count / r.calls) * 100,
  });

  const subjects: CompareSubject[] = useMemo(() => {
    const out: CompareSubject[] = [];
    if (mode === "agents") {
      for (const k of selectedAgentKeys) {
        const r = agentByKey.get(k);
        if (!r) continue;
        out.push(subjectFromAgent(r, "agent", k));
      }
      return out;
    }
    // mode === "team_agents"
    for (const r of teamAgentsRows) {
      const k = agentLookupKey(r) ?? r.agent_id ?? r.email ?? r.full_name ?? "";
      if (!k) continue;
      out.push(subjectFromAgent(r, "team_agent", k));
    }
    return out;
     
  }, [mode, selectedAgentKeys, agentByKey, teamAgentsRows]);

  const bestValues = useMemo(() => {
    const max = (vals: (number | null)[]) => {
      const filtered = vals.filter((v): v is number => v != null);
      return filtered.length === 0 ? null : Math.max(...filtered);
    };
    const min = (vals: (number | null)[]) => {
      const filtered = vals.filter((v): v is number => v != null);
      return filtered.length === 0 ? null : Math.min(...filtered);
    };
    return {
      // Task #377 — Total Calls and Conversion % join the best-value
      // tracker; both are "higher wins" per spec, matching ``calls``
      // / ``quality_pct`` / ``positive_pct``.
      total_calls: max(subjects.map((s) => s.total_calls)),
      calls: max(subjects.map((s) => s.calls)),
      avg_duration_ms: max(subjects.map((s) => s.avg_duration_ms)),
      quality_pct: max(subjects.map((s) => s.quality_pct)),
      positive_pct: max(subjects.map((s) => s.positive_pct)),
      // Lower negative % is better — track the minimum.
      negative_pct: min(subjects.map((s) => s.negative_pct)),
      conversion_pct: max(subjects.map((s) => s.conversion_pct)),
    };
  }, [subjects]);

  const sortedSubjects = useMemo(() => {
    const arr = [...subjects];
    arr.sort((a, b) => {
      const va = a[compareSortKey];
      const vb = b[compareSortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (va < vb) return compareSortDir === "asc" ? -1 : 1;
      if (va > vb) return compareSortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [subjects, compareSortKey, compareSortDir]);

  const COMPARE_SORT_LABELS: Record<CompareSortKey, string> = {
    total_calls: "Total Calls",
    calls: "Analysed Calls",
    avg_duration_ms: "Avg Duration",
    quality_pct: "Quality Call %",
    positive_pct: "Positive %",
    negative_pct: "Negative %",
    conversion_pct: "Conversion %",
  };

  return (
    <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/40"
      >
        <span className="flex items-center gap-2 font-medium text-foreground">
          <span
            className={`inline-block transition-transform duration-[var(--duration-base)] ease-[var(--easing-standard)] ${open ? "rotate-90" : ""}`}
            aria-hidden
          >
            ▶
          </span>
          Compare Agents
          {!open && (selectedAgentKeys.length > 0 || selectedTeamForAgents) && (
            <span className="ml-2 inline-flex rounded-full bg-secondary/15 px-2 py-0.5 text-xs font-medium text-secondary">
              {mode === "team_agents"
                ? `${subjects.length} selected`
                : `${selectedAgentKeys.length} selected`}
            </span>
          )}
        </span>
        <span className="text-xs text-muted-foreground">
          {open ? "Hide" : "Compare agents"}
        </span>
      </button>

      {open && (
        <div className="border-t border-border px-4 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5 text-sm">
              {/* Task #377 — Compare Teams tab removed; only the two
                  agent-centric tabs remain.
                  Task #428 — "Compare Agents in Team" is now first and
                  the default tab; "Compare Agents" follows it. */}
              <button
                type="button"
                onClick={() => switchMode("team_agents")}
                className={`px-3 py-1 rounded ${
                  mode === "team_agents"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground"
                }`}
              >
                Compare Agents in Team
              </button>
              <button
                type="button"
                onClick={() => switchMode("agents")}
                className={`px-3 py-1 rounded ${
                  mode === "agents"
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground"
                }`}
              >
                Compare Agents
              </button>
            </div>
            <button
              type="button"
              onClick={reset}
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            >
              Reset
            </button>
          </div>

          {mode === "agents" ? (
            <div className="space-y-2">
              <div className="relative">
                <input
                  type="text"
                  value={agentSearch}
                  onChange={(e) => setAgentSearch(e.target.value)}
                  placeholder="Search agents to add…"
                  className="w-full border border-input rounded px-2 py-1.5 text-sm"
                />
                {agentSearch && agentSearchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-md border border-border bg-card shadow-lg">
                    {agentSearchResults.map((r) => {
                      const k = agentLookupKey(r)!;
                      return (
                        <button
                          type="button"
                          key={k}
                          onClick={() => {
                            setSelectedAgentKeys((prev) =>
                              prev.includes(k) ? prev : [...prev, k],
                            );
                            setAgentSearch("");
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted/40 flex items-center gap-2"
                        >
                          <AgentAvatar name={r.full_name} email={r.email} />
                          <div className="min-w-0">
                            <div className="font-medium text-foreground truncate">
                              {r.full_name || r.email || "—"}
                            </div>
                            {r.email && (
                              <div className="text-xs text-muted-foreground truncate">
                                {r.email}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {selectedAgentKeys.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedAgentKeys.map((k) => {
                    const r = agentByKey.get(k);
                    const label = r?.full_name || r?.email || k;
                    return (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1 rounded-full bg-secondary/15 px-2 py-0.5 text-xs font-medium text-secondary"
                      >
                        {label}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedAgentKeys((prev) =>
                              prev.filter((x) => x !== k),
                            )
                          }
                          aria-label={`Remove ${label}`}
                          className="text-secondary hover:text-secondary/70 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <select
                value={selectedTeamForAgents}
                onChange={(e) => setSelectedTeamForAgents(e.target.value)}
                className="w-full border border-input rounded px-2 py-1.5 text-sm"
              >
                <option value="">Select a team…</option>
                {myTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.community_name} / {t.name}
                  </option>
                ))}
              </select>
              {selectedTeamForAgents && (
                <div className="text-xs text-muted-foreground">
                  {teamAgentsQuery.isLoading
                    ? "Loading agents…"
                    : `${teamAgentsRows.length} agent${teamAgentsRows.length === 1 ? "" : "s"} on this team`}
                </div>
              )}
            </div>
          )}

          <div>
            {mode === "team_agents" && !selectedTeamForAgents ? (
              <div className="rounded-md border border-dashed border-input px-4 py-6 text-center text-sm text-muted-foreground">
                Select a team to compare its agents.
              </div>
            ) : mode === "team_agents" && teamAgentsQuery.isLoading ? (
              <div className="rounded-md border border-dashed border-input px-4 py-6 text-center text-sm text-muted-foreground">
                Loading agents…
              </div>
            ) : subjects.length === 0 ? (
              <div className="rounded-md border border-dashed border-input px-4 py-6 text-center text-sm text-muted-foreground">
                Select subjects above to start comparing.
              </div>
            ) : subjects.length === 1 ? (
              <div className="rounded-md border border-dashed border-input px-4 py-6 text-center text-sm text-muted-foreground">
                Select at least one more to compare.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-end gap-2 text-sm">
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Sort by
                  </label>
                  <select
                    value={compareSortKey}
                    onChange={(e) =>
                      setCompareSortKey(
                        e.target.value as typeof compareSortKey,
                      )
                    }
                    className="border border-input rounded px-2 py-1 text-sm bg-card"
                  >
                    {(Object.keys(COMPARE_SORT_LABELS) as (typeof compareSortKey)[]).map(
                      (k) => (
                        <option key={k} value={k}>
                          {COMPARE_SORT_LABELS[k]}
                        </option>
                      ),
                    )}
                  </select>
                  <Tooltip
                    content={
                      compareSortDir === "desc"
                        ? "Highest to lowest"
                        : "Lowest to highest"
                    }
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setCompareSortDir((d) => (d === "asc" ? "desc" : "asc"))
                      }
                      aria-label={
                        compareSortDir === "desc"
                          ? "Sort ascending"
                          : "Sort descending"
                      }
                      className="border border-input rounded px-2 py-1 text-sm bg-card hover:bg-muted/40"
                    >
                      {compareSortDir === "desc" ? "↓" : "↑"}
                    </button>
                  </Tooltip>
                </div>
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {sortedSubjects.map((s) => (
                    <CompareCard key={s.id} subject={s} best={bestValues} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function CompareCard({
  subject,
  best,
}: {
  subject: CompareSubject;
  best: {
    total_calls: number | null;
    calls: number | null;
    avg_duration_ms: number | null;
    quality_pct: number | null;
    positive_pct: number | null;
    negative_pct: number | null;
    conversion_pct: number | null;
  };
}) {
  const Metric = ({
    label,
    value,
    isBest,
    formatted,
  }: {
    label: string;
    value: number | null;
    isBest: boolean;
    formatted: string;
  }) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`tabular-nums ${
          isBest && value != null
            ? "font-semibold text-primary"
            : "text-foreground"
        }`}
      >
        {formatted}
      </span>
    </div>
  );

  const fmtPct = (v: number | null) => (v == null ? "—" : `${Math.round(v)}%`);
  const fmtCalls = (v: number) => v.toLocaleString();

  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-md">
      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium bg-secondary/15 text-secondary"
          aria-hidden
        >
          A
        </span>
        <div className="min-w-0">
          <div className="font-medium text-foreground truncate" title={subject.name}>
            {subject.name}
          </div>
          {subject.sublabel && (
            <div className="text-xs text-muted-foreground truncate">
              {subject.sublabel}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        {/* Task #377 — Total Calls pinned to the top; counts every
            call the agent received in the period, viable or not. */}
        <Metric
          label="Total Calls"
          value={subject.total_calls}
          isBest={best.total_calls != null && subject.total_calls === best.total_calls}
          formatted={fmtCalls(subject.total_calls)}
        />
        <Metric
          label="Analysed Calls"
          value={subject.calls}
          isBest={best.calls != null && subject.calls === best.calls}
          formatted={fmtCalls(subject.calls)}
        />
        <Metric
          label="Avg Duration"
          value={subject.avg_duration_ms}
          isBest={
            best.avg_duration_ms != null &&
            subject.avg_duration_ms === best.avg_duration_ms
          }
          formatted={fmtMinutesOneDp(subject.avg_duration_ms)}
        />
        <Metric
          label="Quality Call %"
          value={subject.quality_pct}
          isBest={
            best.quality_pct != null && subject.quality_pct === best.quality_pct
          }
          formatted={fmtPct(subject.quality_pct)}
        />
        <Metric
          label="Positive %"
          value={subject.positive_pct}
          isBest={
            best.positive_pct != null && subject.positive_pct === best.positive_pct
          }
          formatted={fmtPct(subject.positive_pct)}
        />
        <Metric
          label="Negative %"
          value={subject.negative_pct}
          isBest={
            best.negative_pct != null && subject.negative_pct === best.negative_pct
          }
          formatted={fmtPct(subject.negative_pct)}
        />
        {/* Task #377 — Conversion % pinned at the bottom; "—" when
            the agent's team isn't enrolled in any Client Conversion
            assessment or the agent has no analysed calls. */}
        <Metric
          label="Conversion %"
          value={subject.conversion_pct}
          isBest={
            best.conversion_pct != null &&
            subject.conversion_pct === best.conversion_pct
          }
          formatted={fmtPct(subject.conversion_pct)}
        />
      </div>
    </div>
  );
}

export function AgentsView({
  me,
}: {
  me?: UserOut | null;
}) {
  // Task #380 — Brand filter + column are SupaAdmin-only on the Agents
  // View. Non-supadmins behave as "All brands" regardless of state.
  const isSupadmin = me?.role === "supadmin";
  const teamsQ = useQuery<MyTeam[]>({
    queryKey: ["my-teams"],
    queryFn: () => api.myTeams(),
    refetchInterval: false,
  });
  const myTeams = teamsQ.data ?? [];
  const [teamId, setTeamId] = useState<string>("");
  const effectiveTeam = teamId || null;
  const initUrl = useMemo(() => parseUrl(), []);
  const {
    timeRange, rangeMode, customRange, effectiveRangeMode,
    onTimeRangeChange, onRangeModeChange, onCustomRangeChange,
  } = useTimeRange({
    initial: {
      // PET-667 — default the Agents view to "Week" when the URL carries
      // no range. A deep link (?tr=…) still wins: initUrl.timeRange is
      // only null when no range was specified.
      timeRange: initUrl.timeRange ?? "weekly",
      rangeMode: initUrl.rangeMode,
      customRange:
        initUrl.customStart && initUrl.customEnd
          ? { start: initUrl.customStart, end: initUrl.customEnd }
          : null,
    },
    sync: "url",
    urlNavKey: "agents",
  });

  const agentsQ = useQuery<AgentRow[]>({
    queryKey: [
      "agents",
      effectiveTeam,
      timeRange,
      effectiveRangeMode,
      customRange?.start ?? null,
      customRange?.end ?? null,
    ],
    queryFn: () =>
      api.agents(
        effectiveTeam,
        timeRange,
        effectiveRangeMode,
        customRange,
      ),
    refetchInterval: false,
  });

  const [brand, setBrand] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<AgentSortKey>("calls");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const rows = useMemo(() => {
    const all = agentsQ.data ?? [];
    const q = search.trim().toLowerCase();
    const filtered = all.filter((r) => {
      if (isSupadmin && brand !== "all" && r.brand !== brand) return false;
      if (q) {
        const name = (r.full_name || "").toLowerCase();
        if (!name.includes(q)) return false;
      }
      return true;
    });
    const score = (r: AgentRow): number | string => {
      switch (sortKey) {
        case "calls":
          return r.calls;
        case "name":
          return (r.full_name || r.email || "").toLowerCase();
        case "avg_duration_ms":
          return r.avg_duration_ms ?? -1;
        case "quality_pct":
          return r.calls ? r.quality_call_count / r.calls : -1;
        case "positive_pct":
          return r.calls ? r.positive_count / r.calls : -1;
        case "negative_pct":
          return r.calls ? r.negative_count / r.calls : -1;
      }
    };
    const sorted = [...filtered].sort((a, b) => {
      const va = score(a);
      const vb = score(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [agentsQ.data, brand, search, sortKey, sortDir, isSupadmin]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4 shadow-md">
        <TimeRangeControls
          value={timeRange}
          rangeMode={effectiveRangeMode}
          customRange={customRange}
          onValueChange={onTimeRangeChange}
          onRangeModeChange={onRangeModeChange}
          onCustomRangeChange={onCustomRangeChange}
        />
      </div>
      <div className="rounded-lg border border-border bg-card p-4 shadow-md">
        <div className="flex flex-wrap items-end gap-3">
          {myTeams.length > 1 && (
            <div className="flex flex-col">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Team
              </label>
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="border border-input rounded px-2 py-1.5 text-sm mt-1"
              >
                <option value="">All my teams</option>
                {myTeams.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.community_name} / {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isSupadmin && (
            <div className="flex flex-col">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Brand
              </label>
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="border border-input rounded px-2 py-1.5 text-sm mt-1"
              >
                <option value="all">All brands</option>
                {BRAND_OPTIONS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-col flex-1 min-w-[200px]">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by agent name…"
              className="border border-input rounded px-2 py-1.5 text-sm mt-1"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Sort by
            </label>
            <div className="flex items-center gap-1 mt-1">
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as AgentSortKey)}
                className="border border-input rounded px-2 py-1.5 text-sm"
              >
                {(Object.keys(SORT_LABELS) as AgentSortKey[]).map((k) => (
                  <option key={k} value={k}>
                    {SORT_LABELS[k]}
                  </option>
                ))}
              </select>
              <Tooltip content={sortDir === "asc" ? "Ascending" : "Descending"}>
                <button
                  type="button"
                  onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  aria-label={
                    sortDir === "asc" ? "Sort descending" : "Sort ascending"
                  }
                  className="border border-input rounded px-2 py-1.5 text-sm bg-card hover:bg-muted/40"
                >
                  {sortDir === "asc" ? "↑" : "↓"}
                </button>
              </Tooltip>
            </div>
          </div>
          <div className="ml-auto text-sm text-muted-foreground">
            <span className="font-medium">{rows.length}</span> agent
            {rows.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <CompareAgentsPanel
        filteredAgents={rows}
        myTeams={myTeams}
        timeRange={timeRange}
        rangeMode={effectiveRangeMode}
        customRange={customRange}
      />

      {!agentsQ.isLoading && rows.length === 0 ? (
        // Task #198 — align the standalone Calls list empty state with
        // the Task #194 italic muted line used on the Agent Profile's
        // Calls list instead of a bordered card with table chrome.
        // Loading still falls through to the table so the headers and
        // "Loading…" row stay where they were.
        <p className="text-sm text-muted-foreground italic">
          No calls match the current filters.
        </p>
      ) : (
      <section className="rounded-lg border border-border bg-card shadow-md animate-card-enter">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/10 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Agent</th>
                {isSupadmin && <th className="text-left px-3 py-2">Brand</th>}
                <th className="text-right px-3 py-2">
                  <LabelWithTooltip
                    metricId="calls_assessed"
                    className="inline-flex items-center gap-1 justify-end"
                  >
                    Calls
                  </LabelWithTooltip>
                </th>
                <th className="text-right px-3 py-2">
                  <LabelWithTooltip
                    metricId="avg_duration"
                    className="inline-flex items-center gap-1 justify-end"
                  >
                    Avg duration
                  </LabelWithTooltip>
                </th>
                <th className="text-left px-3 py-2">
                  <LabelWithTooltip metricId="quality_call_pct">
                    Quality call %
                  </LabelWithTooltip>
                </th>
                <th className="text-left px-3 py-2">
                  <LabelWithTooltip metricId="positive_pct">
                    Positive %
                  </LabelWithTooltip>
                </th>
                <th className="text-left px-3 py-2">
                  <LabelWithTooltip metricId="negative_pct">
                    Negative %
                  </LabelWithTooltip>
                </th>
              </tr>
            </thead>
            <tbody>
              {agentsQ.isLoading ? (
                <tr>
                  <td
                    colSpan={isSupadmin ? 7 : 6}
                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                  >
                    Loading…
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const qPct = r.calls ? (r.quality_call_count / r.calls) * 100 : 0;
                  const pPct = r.calls ? (r.positive_count / r.calls) * 100 : 0;
                  const nPct = r.calls ? (r.negative_count / r.calls) * 100 : 0;
                  const lookupKey = agentLookupKey(r);
                  // Task #391 — open the agent profile in a new tab via a
                  // real anchor so the Agents list keeps its place and
                  // hover-URL / middle-click / Cmd-click work natively.
                  // The whole row stays clickable using a stretched-link
                  // overlay (``after:absolute after:inset-0``) anchored to
                  // the relatively-positioned row.
                  const href = lookupKey
                    ? buildUrl("agent-profile", agentSlug(r), lookupKey, {
                        timeRange,
                        rangeMode,
                        customStart: customRange?.start ?? null,
                        customEnd: customRange?.end ?? null,
                      })
                    : null;
                  const agentLabelName =
                    r.full_name || r.email || "agent";
                  return (
                    <tr
                      key={r.agent_id ?? r.email ?? r.full_name ?? `row-${idx}`}
                      className={`border-t border-border ${
                        href
                          ? "relative cursor-pointer hover:bg-primary/5 focus-within:bg-primary/5"
                          : ""
                      }`}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <AgentAvatar name={r.full_name} email={r.email} />
                          <div className="min-w-0">
                            {href ? (
                              <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Open ${agentLabelName} profile`}
                                className="font-medium text-foreground truncate block rounded after:absolute after:inset-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                {r.full_name || "—"}
                              </a>
                            ) : (
                              <div className="font-medium text-foreground truncate">
                                {r.full_name || "—"}
                              </div>
                            )}
                            {r.email && (
                              <div className="text-xs text-muted-foreground truncate">
                                {r.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      {isSupadmin && (
                        <td className="px-3 py-2">
                          <BrandPill brand={r.brand} />
                        </td>
                      )}
                      <td className="px-3 py-2 text-right tabular-nums">{r.calls}</td>
                      <td className="px-3 py-2 text-right text-xs text-muted-foreground tabular-nums">
                        {fmtMinutesOneDp(r.avg_duration_ms)}
                      </td>
                      <td className="px-3 py-2">
                        <MetricBar pct={qPct} kind="quality" />
                      </td>
                      <td className="px-3 py-2">
                        <MetricBar pct={pPct} kind="positive" />
                      </td>
                      <td className="px-3 py-2">
                        <MetricBar pct={nPct} kind="negative" />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
      )}
    </div>
  );
}
